// Pure, dependency-free WhatsApp text formatting — kept separate from printUtils.js
// (which pulls in Capacitor BLE/Filesystem/Share) so the direct-to-customer chat
// link can be built synchronously inside a click handler, with no dynamic import
// delay that could cause the browser to block window.open() as a popup.

const fmt2 = (n) => Number(n ?? 0).toFixed(2);

// Fixed width, not content-based — a separator sized to the longest line (e.g. a
// long customer name) fits fine on WhatsApp Web's wide bubble but wraps onto two
// lines on a narrow mobile screen. A short fixed length is safe on every device.
const WA_SEP = '┄'.repeat(24);

export function formatWhatsAppMessage(sale, storeProfile) {
  const store    = storeProfile?.shop_name || 'Store';
  const bill     = sale.bill_number && sale.bill_number !== 'PENDING' ? sale.bill_number : '—';
  const date     = sale.created_at
    ? new Date(sale.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';
  const items    = sale.items ?? [];
  const subtotal = sale.subtotal ?? items.reduce((s, i) => s + (i.item_total ?? 0), 0);
  const discount = sale.overall_discount ?? 0;
  const payment  = sale.payment_mode === 'split' && sale.split_payments?.length
    ? sale.split_payments.filter(p => p.amount > 0).map(p => `${p.mode.toUpperCase()} ₹${fmt2(p.amount)}`).join(' + ')
    : (sale.payment_mode || 'CASH').toUpperCase();

  const headerLines = [`*${store.toUpperCase()}*`, `Bill *${bill}* · ${date}`];
  if (sale.customer_name) headerLines.push(`Customer: ${sale.customer_name}`);

  const itemLines = items.map((item) => {
    const disc = item.discount > 0 ? ` (-${item.discount}%)` : '';
    return `${item.product_name || ''}${disc}  x${item.quantity}  ₹${fmt2(item.item_total)}`;
  });

  const totalLines = [];
  if (discount > 0) totalLines.push(`Subtotal: ₹${fmt2(subtotal)}`);
  if (discount > 0) totalLines.push(`Cart Discount: -₹${fmt2(discount)}`);
  totalLines.push(`*TOTAL: ₹${fmt2(sale.total_amount)}*`);
  totalLines.push(`Payment: ${payment}`);

  // Mirrors the khata summary on the visual receipt (ReceiptModal.js lines ~198-238) — keep in sync.
  const khataLines = [];
  if (sale.customer_name) {
    const khataCleared = sale.khata_cleared ?? 0;
    const khataAdded   = sale.khata_added ?? (
      sale.payment_mode === 'khata'
        ? sale.total_amount
        : (sale.split_payments?.reduce((s, p) => p.mode === 'khata' ? s + p.amount : s, 0) ?? 0)
    );
    const existingKhata = sale.existing_khata_balance
      ?? (khataCleared > 0 ? (sale.khata_remaining ?? 0) + khataCleared : 0);
    const netChange     = khataAdded - khataCleared;
    const newBalance    = existingKhata + netChange;
    const changeLabel   = khataAdded > 0 ? 'Khata Added'
                        : khataCleared > 0 ? 'Khata Cleared'
                        : 'Khata Added/Cleared';
    const changeDisplay = netChange > 0 ? `+₹${fmt2(khataAdded)}`
                        : netChange < 0 ? `-₹${fmt2(khataCleared)}`
                        : '0';
    khataLines.push(`${sale.customer_name} Existing Khata: ₹${fmt2(existingKhata)}`);
    khataLines.push(`${sale.customer_name} ${changeLabel}: ${changeDisplay}`);
    khataLines.push(`Outstanding Balance: ₹${fmt2(newBalance)}`);
    khataLines.push(`*TOTAL COLLECTION: ₹${fmt2(sale.total_amount + khataCleared)}*`);
  }

  const lines = [...headerLines, WA_SEP, ...itemLines, WA_SEP, ...totalLines];
  if (khataLines.length) lines.push(WA_SEP, ...khataLines);
  lines.push('');
  lines.push(storeProfile?.receipt_footer || 'Thank you for shopping with us!');
  lines.push(`_Powered by *KiranaFlow* · kiranaflow.com_`);
  return lines.join('\n');
}

// Strips formatting and validates a 10-digit Indian mobile number, returning
// the wa.me-ready "91XXXXXXXXXX" form, or null if missing/invalid.
export function formatPhoneForWhatsApp(mobile) {
  if (!mobile) return null;
  const last10 = String(mobile).replace(/\D/g, '').slice(-10);
  return /^[6-9]\d{9}$/.test(last10) ? `91${last10}` : null;
}
